import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, X, Image, FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  bucket: 'client-logos' | 'people-avatars' | 'user-avatars' | 'invoice-pdfs';
  className?: string;
  variant?: 'square' | 'circle';
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

export function ImageUpload({ 
  value, 
  onChange, 
  bucket, 
  className,
  variant = 'square',
  size = 'md',
  label 
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  const uploadImage = async (file: File) => {
    try {
      setIsUploading(true);
      
      // Validate file type based on bucket
      if (bucket === 'invoice-pdfs') {
        if (!file.type.includes('pdf')) {
          throw new Error('Please select a PDF file');
        }
        // Validate PDF file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('PDF must be less than 10MB');
        }
      } else {
        // Validate image file type
        if (!file.type.startsWith('image/')) {
          throw new Error('Please select an image file');
        }
        // Validate image file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('Image must be less than 5MB');
        }
      }

      const ext = file.name.split('.').pop();
      const filePath = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext ?? (bucket === 'invoice-pdfs' ? 'pdf' : 'png')}`;
      
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, { upsert: true });
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);
      
      onChange(publicUrl);
      
      toast({
        title: "Success",
        description: `${bucket === 'invoice-pdfs' ? 'PDF' : 'Image'} uploaded successfully`,
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message || `Could not upload ${bucket === 'invoice-pdfs' ? 'PDF' : 'image'}`,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImage(file);
    }
  };

  const removeImage = () => {
    onChange('');
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <div className="flex items-center gap-4">
        {/* Image preview */}
        <div 
          className={cn(
            "border-2 border-dashed border-border flex items-center justify-center bg-muted",
            sizeClasses[size],
            variant === 'circle' ? 'rounded-full' : 'rounded-lg',
            className
          )}
        >
          {value ? (
            <div className="relative w-full h-full">
              {bucket === 'invoice-pdfs' ? (
                <div className={cn(
                  "w-full h-full flex flex-col items-center justify-center bg-muted border border-border",
                  variant === 'circle' ? 'rounded-full' : 'rounded-lg'
                )}>
                  <FileText className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-xs text-muted-foreground text-center px-2">PDF Uploaded</span>
                </div>
              ) : (
                <img 
                  src={value} 
                  alt="Preview" 
                  className={cn(
                    "w-full h-full object-cover",
                    variant === 'circle' ? 'rounded-full' : 'rounded-lg'
                  )}
                />
              )}
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full p-0"
                onClick={removeImage}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            bucket === 'invoice-pdfs' ? (
              <FileText className="w-8 h-8 text-muted-foreground" />
            ) : (
              <Image className="w-8 h-8 text-muted-foreground" />
            )
          )}
        </div>

        {/* Upload controls */}
        <div className="flex-1">
          <Input
            type="file"
            accept={bucket === 'invoice-pdfs' ? "application/pdf" : "image/*"}
            onChange={handleFileSelect}
            disabled={isUploading}
            className="hidden"
            id={`image-upload-${bucket}`}
          />
          <Label
            htmlFor={`image-upload-${bucket}`}
            className="cursor-pointer"
          >
            <Button
              type="button"
              variant="outline"
              disabled={isUploading}
              asChild
            >
              <span>
                <Upload className="w-4 h-4 mr-2" />
                {isUploading ? 'Uploading...' : `Upload ${bucket === 'invoice-pdfs' ? 'PDF' : 'Image'}`}
              </span>
            </Button>
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            {bucket === 'invoice-pdfs' ? 'PDF up to 10MB' : 'JPG, PNG, WebP up to 5MB'}
          </p>
        </div>
      </div>
    </div>
  );
}